import React from 'react';
import PropTypes from 'prop-types';
import { Box, InputAdornment, MenuItem, TextField, Stack } from '@mui/material';
import ConfigCard from './ConfigCard';
import { PdiRow as Row } from './_PdiHelpers';

/**
 * "Navigation menu" card — picks which category level drives the top menu
 * plus a few sizing controls.
 *
 * - `categoriesShown` round-trips as a single string '0'..'7' (legacy
 *   NAVBAR_ITEMS_TO_LIST). State keeps an array shape (`[key]`) so the
 *   save flow in WebshopConfigScreen can stay unchanged.
 * - `category_N` labels come from `shopFieldLabels` (kasvars CodeNOms)
 *   when populated; otherwise fall back to the cfg_pdi_details_category_N
 *   i18n string.
 * - `itemsPerRow` ↔ HEADER_ITEM_BREAK_AMOUNT — how many menu items fit on
 *   one row before the rest wrap to a new line.
 * - `menuHeight` ↔ HEADER_FILTER_HEIGHT, `menuTextSize` ↔ HEADER_FILTER_FONT_SIZE.
 */

const NavigationMenuCard = ({ value = {}, onChange, shopFieldLabels, theme, t }) => {
    const tr = (key, fallback) => (typeof t === 'function' ? t(key) : fallback);
    const get = (k, fb) => (value[k] !== undefined ? value[k] : fb);
    const set = (k) => (v) => onChange && onChange(k, v);

    const labelForCategory = (n) => {
        const key = `category_${n}`;
        const shopLabel = shopFieldLabels && shopFieldLabels[key];
        if (shopLabel) return shopLabel;
        return tr(`cfg_pdi_details_${key}`, `Category ${n}`);
    };

    // Modal stores categoriesShown as a one-element array (the save flow
    // serialises that to NAVBAR_ITEMS_TO_LIST). UI surfaces it as a single
    // select; '0' is the explicit "no category" option.
    const currentList    = Array.isArray(get('categoriesShown')) ? get('categoriesShown') : [];
    const currentKey     = currentList[0] || '0';
    const onCategoryPick = (val) => set('categoriesShown')(val === '0' ? [] : [val]);

    return (
        <ConfigCard
            anchor="cfg-card-menu-navigation"
            title={tr('cfg_nav_menu_title', 'Navigation menu')}
            description={tr('cfg_nav_menu_desc', 'The menu visitors use to browse your product categories.')}
            theme={theme}
        >
            <Stack divider={<Box sx={{ borderTop: '1px solid', borderColor: 'divider' }} />}>
                <Row
                    label={tr('cfg_nav_menu_categories', 'Categories shown in the menu')}
                    help={tr('cfg_nav_menu_categories_help', 'Pick which category tree drives your main navigation')}
                    control={
                        <TextField
                            select
                            size="small"
                            value={currentKey}
                            onChange={(e) => onCategoryPick(e.target.value)}
                            sx={{ minWidth: 220 }}
                        >
                            <MenuItem value="0">{tr('cfg_nav_menu_categories_none', 'None')}</MenuItem>
                            {[1,2,3,4,5,6,7].map(n => (
                                <MenuItem key={n} value={String(n)}>{labelForCategory(n)}</MenuItem>
                            ))}
                        </TextField>
                    }
                />

                <Row
                    label={tr('cfg_nav_menu_items_per_row', 'Items per row')}
                    help={tr('cfg_nav_menu_items_per_row_help', 'When the menu has more items than this, the rest wrap to a new row')}
                    control={
                        <TextField
                            type="number"
                            size="small"
                            value={get('itemsPerRow', 6) ?? 6}
                            onChange={(e) => set('itemsPerRow')(Math.max(1, parseInt(e.target.value) || 1))}
                            InputProps={{ endAdornment: <InputAdornment position="end">{tr('cfg_nav_menu_items_unit', 'items')}</InputAdornment> }}
                            sx={{ width: 130 }}
                        />
                    }
                />

                <Row
                    label={tr('cfg_nav_menu_height', 'Menu height')}
                    help={tr('cfg_nav_menu_height_help', 'How tall the menu bar is')}
                    control={
                        <TextField
                            type="number"
                            size="small"
                            value={get('menuHeight', 50) ?? 50}
                            onChange={(e) => set('menuHeight')(Math.max(20, parseInt(e.target.value) || 0))}
                            InputProps={{ endAdornment: <InputAdornment position="end">{tr('cfg_nav_menu_pixels_unit', 'pixels')}</InputAdornment> }}
                            sx={{ width: 130 }}
                        />
                    }
                />

                <Row
                    label={tr('cfg_nav_menu_text_size', 'Menu text size')}
                    help={tr('cfg_nav_menu_text_size_help', 'Font size for menu items')}
                    control={
                        <TextField
                            type="number"
                            size="small"
                            value={get('menuTextSize', 14) ?? 14}
                            onChange={(e) => set('menuTextSize')(Math.max(8, parseInt(e.target.value) || 0))}
                            InputProps={{ endAdornment: <InputAdornment position="end">{tr('cfg_nav_menu_pixels_unit', 'pixels')}</InputAdornment> }}
                            sx={{ width: 130 }}
                        />
                    }
                />
            </Stack>
        </ConfigCard>
    );
};

NavigationMenuCard.propTypes = {
    value:           PropTypes.object,
    onChange:        PropTypes.func,
    shopFieldLabels: PropTypes.object,
    theme:           PropTypes.string,
    t:               PropTypes.func,
};

export default NavigationMenuCard;
